/**
 * Prompt Share Dialog
 * Share personal prompts with other users (creates independent copies)
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Users, Loader2 } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { sharePrompt, type UserPrompt } from '@/services/prompt-library-service'
import { getCachedUser } from '@/services/auth-service'
import { fetchClientUsers, type ClientUser } from '@/services/client-users-service'

interface PromptShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  prompt: UserPrompt | null
  onSuccess?: () => void
}

export function PromptShareDialog({
  open,
  onOpenChange,
  prompt,
  onSuccess,
}: PromptShareDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()

  const [clientUsers, setClientUsers] = useState<ClientUser[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setSelectedUserIds([])
      const user = getCachedUser()
      if (user) {
        setCurrentUserId(user.id)
      }
    }
  }, [open])

  useEffect(() => {
    if (currentUserId !== null) {
      loadClientUsers()
    }
  }, [currentUserId])

  const loadClientUsers = async () => {
    setLoading(true)
    try {
      // No application filter here — prompts can be shared with all client
      // users, not just those with webOS access.
      const result = await fetchClientUsers({ application: null, throwOnError: true })
      // null = missing token or non-OK response: keep the previously
      // loaded list (and stay silent), matching the original behavior.
      if (result !== null) {
        const users: ClientUser[] = result
          .filter((u) => u.id !== currentUserId)
          .map((u) => ({
            id: u.id,
            email: u.email || `User ${u.id.slice(0, 8)}`,
            client_id: u.client_id,
          }))
        setClientUsers(users)
      }
    } catch (error) {
      console.error('Failed to load users:', error)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('promptLibrary.share.loadUsersError'),
      })
    } finally {
      setLoading(false)
    }
  }

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    )
  }

  const handleShare = async () => {
    if (!prompt || selectedUserIds.length === 0) return

    setSharing(true)
    try {
      const sharedCount = await sharePrompt(prompt.id, selectedUserIds)

      // cdb_tqs notifies the recipients in the same request (#1091).
      toast({
        title: t('common.success'),
        description: t('promptLibrary.share.shareSuccess', { count: sharedCount }),
      })

      setSelectedUserIds([])
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Failed to share prompt:', error)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('promptLibrary.share.shareError'),
      })
    } finally {
      setSharing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('promptLibrary.share.title')}
          </DialogTitle>
          <DialogDescription>
            {t('promptLibrary.share.description')}
          </DialogDescription>
        </DialogHeader>

        {prompt && (
          <div className="space-y-4 py-4">
            {/* Prompt Info */}
            <div className="p-3 rounded-lg border border-[var(--window-border-color)] bg-[var(--window-background)]">
              <h3 className="font-semibold text-sm">{prompt.title}</h3>
              {prompt.description && (
                <p className="text-xs text-[var(--text-secondary)] mt-1">{prompt.description}</p>
              )}
            </div>

            {/* User Selection */}
            <div className="space-y-2">
              <Label>
                {t('promptLibrary.share.selectUsers')} ({selectedUserIds.length})
              </Label>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[var(--text-secondary)]" />
                </div>
              ) : clientUsers.length === 0 ? (
                <p className="text-sm text-[var(--text-secondary)] py-4">
                  {t('promptLibrary.share.noUsersAvailable')}
                </p>
              ) : (
                <div className="border border-[var(--window-border-color)] rounded-lg max-h-64 overflow-y-auto bg-[var(--window-background)]">
                  {clientUsers.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 p-3 border-b border-[var(--window-border-color)] last:border-b-0 cursor-pointer hover:bg-[var(--menu-item-hover)]"
                    >
                      <Checkbox
                        checked={selectedUserIds.includes(user.id)}
                        onCheckedChange={() => handleToggleUser(user.id)}
                      />
                      <span className="text-sm flex-1 text-[var(--text-color)]">{user.email}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sharing}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleShare}
            disabled={sharing || selectedUserIds.length === 0}
          >
            {sharing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('promptLibrary.share.sharing')}
              </>
            ) : (
              <>
                <Users className="mr-2 h-4 w-4" />
                {t('promptLibrary.share.shareButton', { count: selectedUserIds.length })}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
